import React from 'react';
import { X, AlertTriangle, Info, ExternalLink } from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';
import { DependencyInfo, DependencyService } from '../../services/dependencyService';

interface DeletionProtectionModalProps {
  isOpen: boolean;
  onClose: () => void;
  entityName: string;
  entityType: string;
  dependencies: DependencyInfo[];
  onViewDetails?: (dependency: DependencyInfo) => void;
}

const DeletionProtectionModal: React.FC<DeletionProtectionModalProps> = ({
  isOpen,
  onClose,
  entityName,
  entityType,
  dependencies,
  onViewDetails
}) => {
  if (!isOpen) return null;

  const groupedDependencies = dependencies.reduce((acc, dep) => {
    if (!acc[dep.entityType]) {
      acc[dep.entityType] = [];
    }
    acc[dep.entityType].push(dep);
    return acc;
  }, {} as Record<string, DependencyInfo[]>);

  const totalDependencies = dependencies.length;

  return (
    <AnimatePresence>
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4"
      >
        <motion.div
          initial={{ scale: 0.95, opacity: 0 }}
          animate={{ scale: 1, opacity: 1 }}
          exit={{ scale: 0.95, opacity: 0 }}
          className="bg-[var(--color-surface)] text-[var(--color-text)] border border-[var(--color-border)] rounded-lg shadow-2xl w-full max-w-2xl max-h-[90vh] flex flex-col"
        >
          {/* Header */}
          <div className="flex items-center justify-between p-6 border-b border-[var(--color-border)] bg-gradient-to-r from-red-600 to-red-700 text-white rounded-t-lg">
            <div className="flex items-center space-x-3">
              <AlertTriangle className="w-6 h-6" />
              <div>
                <h2 className="text-xl font-semibold">Cannot Delete {entityName}</h2>
                <p className="text-red-100 text-sm">
                  This {DependencyService.getEntityTypeDisplayName(entityType).toLowerCase()} is being used in other parts of the system
                </p>
              </div>
            </div>
            <button
              onClick={onClose}
              className="text-white hover:text-gray-200 transition-colors p-1 rounded-full hover:bg-white hover:bg-opacity-20"
            >
              <X className="w-5 h-5" />
            </button>
          </div>

          {/* Content */}
          <div className="flex-1 overflow-auto p-6 text-[var(--color-text)]">
            <div className="mb-6">
              <div className="flex items-center space-x-2 mb-4">
                <Info className="w-5 h-5 text-blue-500" />
                <h3 className="text-lg font-semibold text-[var(--color-text)]">
                  Dependencies Found ({totalDependencies})
                </h3>
              </div>
              
              <div className="bg-blue-50 dark:bg-blue-900/30 border border-blue-200 dark:border-blue-800 rounded-lg p-4 mb-6">
                <p className="text-blue-800 dark:text-blue-200 text-sm">
                  <strong>{entityName}</strong> cannot be deleted because it is referenced in{' '}
                  <strong>{totalDependencies}</strong> other item{totalDependencies > 1 ? 's' : ''} in the system.
                </p>
              </div>
            </div>

            {/* Dependencies List */}
            <div className="space-y-4">
              {Object.entries(groupedDependencies).map(([type, deps]) => (
                <div key={type} className="border border-[var(--color-border)] rounded-lg overflow-hidden bg-[var(--color-surface)]">
                  <div className="bg-[var(--color-background)] px-4 py-3 border-b border-[var(--color-border)]">
                    <div className="flex items-center justify-between">
                      <h4 className="font-semibold text-[var(--color-text)]">
                        {DependencyService.getEntityTypeDisplayName(type)}
                      </h4>
                      <span className="text-sm text-[var(--color-text-secondary)] bg-[var(--color-surface)] border border-[var(--color-border)] px-2 py-1 rounded">
                        {deps.length} item{deps.length > 1 ? 's' : ''}
                      </span>
                    </div>
                  </div>
                  
                  <div className="divide-y divide-[var(--color-border)]">
                    {deps.map((dep, index) => (
                      <div key={index} className="px-4 py-3 hover:bg-[var(--color-background)] transition-colors">
                        <div className="flex items-center justify-between">
                          <div className="flex-1">
                            <div className="flex items-center space-x-2">
                              <span className="font-medium text-[var(--color-text)]">
                                {dep.entityName}
                              </span>
                              <span className={`text-xs px-2 py-1 rounded-full ${
                                dep.usageType === 'direct' 
                                  ? 'bg-green-100 text-green-800 dark:bg-green-900/40 dark:text-green-300' 
                                  : 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900/40 dark:text-yellow-300'
                              }`}>
                                {DependencyService.getUsageTypeDisplayName(dep.usageType)}
                              </span>
                            </div>
                            {dep.description && (
                              <p className="text-sm text-[var(--color-text-secondary)] mt-1">
                                {dep.description}
                              </p>
                            )}
                          </div>
                          
                          {onViewDetails && (
                            <button
                              onClick={() => onViewDetails(dep)}
                              className="ml-4 p-2 text-[var(--color-text-secondary)] hover:text-blue-500 hover:bg-[var(--color-background)] rounded transition-colors"
                              title="View Details"
                            >
                              <ExternalLink className="w-4 h-4" />
                            </button>
                          )}
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* Footer */}
          <div className="flex items-center justify-between p-6 border-t border-[var(--color-border)] bg-[var(--color-background)] rounded-b-lg">
            <div className="text-sm text-[var(--color-text-secondary)]">
              To delete this item, you must first remove or update all references to it.
            </div>
            
            <div className="flex items-center space-x-3">
              <button
                onClick={onClose}
                className="px-4 py-2 text-[var(--color-text-secondary)] hover:text-[var(--color-text)] transition-colors"
              >
                Close
              </button>
            </div>
          </div>
        </motion.div>
      </motion.div>
    </AnimatePresence>
  );
};

export default DeletionProtectionModal;
